import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useOrderByIdQuery } from '../../../Store/Apis/bookingApi';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import formatDate from '../../../Hooks/formatDate';
import { useTaxPercentageQuery } from '../../../Store/Apis/toolsApi';

const PrintQuotation = () => {
    const user = useSelector((state) => state.auth.user)
    const [logoLoaded, setLogoLoaded] = useState(false); // Track logo loading state
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const orderId = queryParams.get('id');

    const orderByIdApi = useOrderByIdQuery({
        keyID: orderId
    });

    const taxPercentageApi = useTaxPercentageQuery();

    const taxPercentage = Number(taxPercentageApi?.data?.data?.masterValue) || 0; // Get the tax value or default to 0
    const taxAmount = orderByIdApi?.data?.data[0].subTotal * taxPercentage / 100;
    const subtotal = !taxPercentageApi.isSuccess 
        ? orderByIdApi?.data?.data[0].subTotal 
        : orderByIdApi?.data?.data[0].subTotal + taxAmount;
    const grossTotal = Number(subtotal) - Number(orderByIdApi?.data?.data[0]?.discount);


    useEffect(() => {
        // Hide sidebar and header by adding CSS classes
        document.querySelector('aside.sidenav').style.display = 'none';
        document.querySelector('.sidenav.fixed-start + .main-content').style.marginLeft = '0';
        document.querySelector('.navbar-main').style.display = 'none';
    }, []);

    useEffect(() => {
        if (orderByIdApi.isSuccess && logoLoaded) {
            // Open print dialog on success
            window.print();

            // Close the page when the print dialog is closed
            const handleAfterPrint = () => {
                window.close();
            };

            window.addEventListener('afterprint', handleAfterPrint);

            return () => {
                window.removeEventListener('afterprint', handleAfterPrint);
            };
        }
    }, [orderByIdApi.isSuccess, window, logoLoaded]);

    if (!orderByIdApi.isSuccess) {
        return null;
    }

    return (
        <div className='card' id="invoice">
            <div className="card-body">
                <div className="page-wrapper" style={{ padding: 36 }}>
                    <table className="table table-borderless">
                        <thead>
                            <tr>
                                <td colSpan="3">
                                    <img src={user?.logo} alt="" height={70} className='mb-2' onLoad={() => setLogoLoaded(true)} />
                                    <h5 className="text-uppercase fw-600 mb-1">{orderByIdApi.data.data[0].outletName}</h5>
                                    <p className="mb-0">{orderByIdApi.data.data[0].outletPhone}</p>
                                    <p className="mb-0">{orderByIdApi.data.data[0].outletAddress}</p>
                                </td>
                                <td colSpan="2" className="text-end">
                                    <h6 className="fw-bold">
                                        <span>Quotation ID -</span>
                                        <span className="ms-2">{orderByIdApi.data.data[0].orderNumber}</span>
                                    </h6>
                                    <p className="mb-1">
                                        <span>Order Date:</span>
                                        <span className="fw-600 ms-2">{formatDate(orderByIdApi.data.data[0].orderDate)}</span>
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="5" className="text-center">
                                    <h4 className="text-uppercase my-3">Quotation</h4>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-light">
                                <th className="text-uppercase text-secondary text-dark px-3 text-sm">#</th>
                                <th className="text-uppercase text-secondary text-dark text-sm">Data</th>
                                <th className="text-uppercase text-secondary text-dark text-sm">Rate</th>
                                <th className="text-uppercase text-secondary text-dark text-sm">QTY</th>
                                <th className="text-uppercase text-secondary text-dark text-sm">Total</th>
                            </tr>
                            {orderByIdApi.data.data[0].serviceTypeAndName.map((service, index) => (
                                <tr key={service.serviceID}>
                                    <td className="px-3 pb-5">{index + 1}</td>
                                    <td>
                                        <h6 className="mb-0">{service.serviceName}</h6>
                                        <p className="mb-0 text-sm">{service.productServiceName}</p>
                                        <p className="mb-0 text-sm">{service.serviceCode}</p>
                                    </td>
                                    <td>{service.rate}</td>
                                    <td>{service.quantity}</td>
                                    <td>{service.rate * service.quantity}</td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan="3">
                                    <h6 className="fw-500">Quotation To:</h6>
                                    <p style={{marginBottom: 3}}>{orderByIdApi.data.data[0].customerName}</p>
                                    <p style={{marginBottom: 3}}>{orderByIdApi.data.data[0].customerNumber}</p>
                                    <p style={{marginBottom: 3}}>{orderByIdApi.data.data[0].email}</p>
                                    <p style={{marginBottom: 3}}>GST No: {orderByIdApi.data.data[0].gstNo}</p>
                                    <p style={{marginBottom: 3}}>Notes: </p>
                                </td>
                                <td colSpan="2" className="align-top">
                                    <h6 className="fw-500 mb-2">Payment Details:</h6>
                                    <div className="d-flex justify-content-between">
                                        <span>Sub Total:</span>
                                        <span>R {orderByIdApi.data.data[0].subTotal}.00</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Discount:</span>
                                        <span>R {orderByIdApi.data.data[0].discount}.00</span>
                                    </div>
                                    <div className="row mb-50 align-items-center">
                                        <div className="col text-sm">Vat ({taxPercentage}%):</div>
                                        <div className="col-auto text-sm">
                                            R {taxAmount.toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="row align-items-center">
                                        <div className="col text-sm fw-600">Gross Total:</div>
                                        <div className="col-auto text-sm text-dark fw-600">
                                            R {grossTotal.toFixed(2)}
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <div className="fw-bold">
                                            <span>Paid Amount: </span>
                                            <span>
                                                R {Number(orderByIdApi.data.data[0].amountPaid).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="fw-bold">
                                            <span>Due Amount: </span>
                                            <span>
                                                R {(
                                                    Number(grossTotal) -
                                                    Number(orderByIdApi.data.data[0].amountPaid)
                                                ).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p className='text-center text-sm'>
                        Terms and Conditions printed overleaf <br /> Powered by <b>Gert Sibande</b>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default PrintQuotation;
